import { forwardRef, Fragment, useImperativeHandle } from 'react'
import { Controller, useForm } from 'react-hook-form'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { Skeleton } from '@material-ui/lab'
import { Box, Chip, TextField, Typography } from '@material-ui/core'
import { useTheme } from '@material-ui/styles'

import { Card, Select } from 'components'

import schema from './schema'
import useStyles from './styles'
import constants from 'constants/index'

const RegulationForm = forwardRef(
  (
    {
      onSubmit = () => {},
      type = constants.regulations.FORM_TYPES.EDIT,
      regulation = {},
      isLoading = false,
    },
    ref,
  ) => {
    const theme = useTheme()
    const classes = useStyles()

    const { STATUSES, STATUS_LABEL } = constants.regulations

    const STATUS_CLASS = {
      [STATUSES.ACTIVE]: classes.active,
      [STATUSES.INACTIVE]: classes.inactive,
      [STATUSES.DRAFT]: classes.draft,
    }

    const { control, handleSubmit, errors } = useForm({
      validationSchema: schema,
      defaultValues: {
        title: regulation?.title || '',
        description: regulation?.description || '',
        platform: regulation?.platform || '',
      },
    })

    const disabled = type === constants.regulations.FORM_TYPES.SHOW

    useImperativeHandle(ref, () => ({
      submit: () =>
        new Promise((resolve) => {
          handleSubmit(resolve, () => resolve(null))()
        }),
    }))

    return (
      <Card
        title={
          <Box className={classes.titleBox}>
            <Typography variant="h5">Informações gerais</Typography>
            {regulation?.status && (
              <Chip
                className={clsx(classes.chip, STATUS_CLASS[regulation?.status])}
                size="small"
                label={STATUS_LABEL[regulation?.status] || 'Indefinido'}
              />
            )}
          </Box>
        }
        paddingCard={0}
      >
        <Box
          component="form"
          display="flex"
          flexDirection="column"
          gridGap={theme.spacing(2)}
          py={1}
          onSubmit={handleSubmit(onSubmit)}
        >
          {isLoading ? (
            <Fragment>
              <Skeleton variant="rect" height={54} width="100%" />
              <Skeleton variant="rect" height={104} width="100%" />
              <Skeleton variant="rect" height={54} width="100%" />
            </Fragment>
          ) : (
            <Fragment>
              <Controller
                as={
                  <TextField
                    fullWidth
                    label="Título"
                    error={!!errors?.title}
                    helperText={errors?.title?.message}
                    disabled={disabled}
                  />
                }
                control={control}
                name="title"
                mode="onBlur"
              />
              <Controller
                as={
                  <TextField
                    fullWidth
                    label="Descrição"
                    multiline
                    rows={4}
                    error={!!errors?.description}
                    helperText={errors?.description?.message}
                    disabled={disabled}
                  />
                }
                control={control}
                name="description"
                mode="onBlur"
              />
              <Controller
                control={control}
                name="platform"
                mode="onBlur"
                as={
                  <Select
                    label="Plataforma"
                    items={constants.regulations.PLATFORMS_OPTIONS}
                    error={!!errors?.platform}
                    helperText={errors?.platform?.message}
                    disabled={disabled}
                  />
                }
              />
            </Fragment>
          )}
        </Box>
      </Card>
    )
  },
)

RegulationForm.propTypes = {
  onSubmit: PropTypes.func,
  type: PropTypes.oneOf(Object.values(constants.regulations.FORM_TYPES)),
  regulation: PropTypes.object,
  isLoading: PropTypes.bool,
}

export default RegulationForm
